/**
 * ConversationSummaryCard Component
 * Displays the AI conversation summary with expand/collapse functionality.
 */

import { FC, useState } from 'react';
import { MessageSquareText } from 'lucide-react';

interface ConversationSummaryCardProps {
  summary: string;
  maxLength?: number;
}

export const ConversationSummaryCard: FC<ConversationSummaryCardProps> = ({
  summary,
  maxLength = 150,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = summary.length > maxLength;
  const shown =
    shouldTruncate && !isExpanded ? `${summary.slice(0, maxLength).trimEnd()}…` : summary;

  return (
    <div className="relative rounded-lg border border-amber-500/25 bg-amber-500/[0.06] dark:border-amber-400/20 dark:bg-amber-400/[0.07] pl-3.5 pr-3 py-1.5">
      {/* Accent rail */}
      <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-amber-500/60 dark:bg-amber-400/60" />

      <div className="mb-1.5 flex items-center gap-1.5">
        <MessageSquareText className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700/90 dark:text-amber-400/90">
          Conversation Summary
        </span>
      </div>

      <p className="text-xs sm:text-[13px] leading-relaxed text-foreground/80">{shown}</p>

      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="mt-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default ConversationSummaryCard;
